import React, { useState } from 'react';
import { Alert, Box } from '@mui/material';
import { toast } from 'react-toastify';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import AddIcon from '@mui/icons-material/Add';
import LowPriorityIcon from '@mui/icons-material/LowPriority';

import { MenuItem } from '@tassomutuo-backoffice/types';
import {
  ConfirmActionModal,
  CtaButton,
  EmptySectionMessage,
  PageContentLayout,
} from '@tassomutuo-backoffice/ui';
import { getAllPages } from '../../../services/api/page/page';
import {
  createFooterItem,
  deleteFooterItem,
  getFooterItems,
  updateFooterItem,
  changeFooterItemsOrder,
  publishFooterItemsInProd,
  getFooterItemsInProd,
} from '../../../services/api/menu/footer';
import configEnv from '../../../config/config';
import MenuVoiceFormModal from './components/MenuVoiceFormModal';
import MenuItemCard from './components/MenuItemCard';
import MenuVoiceConfirmDeleteModal from './components/MenuVoiceConfirmDeleteModal';
import MenuVoiceOrderModal from './components/MenuVoiceOrderModal';

const NavigationFooterPage = () => {
  const queryClient = useQueryClient();

  const {
    isPending: pagesIsPending,
    data: pages,
    error: pagesError,
  } = useQuery({
    queryKey: ['Pages'],
    queryFn: getAllPages,
  });

  const {
    isPending: menuItemsIsPending,
    data: menuItems,
    error: menuItemsError,
  } = useQuery({
    queryKey: ['MenuItems'],
    queryFn: getFooterItems,
  });

  const { data: prodMenuItems } = useQuery({
    queryKey: ['MenuItemsProd'],
    queryFn: getFooterItemsInProd,
  });

  const createMenuItemMutation = useMutation({
    mutationFn: createFooterItem,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: ['MenuItems'],
      });
      toast.success('Voce di menu creata con successo!');
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const updateMenuItemMutation = useMutation({
    mutationFn: updateFooterItem,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: ['MenuItems'],
      });
      toast.success('Voce di menu aggiornata con successo!');
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const deleteMenuItemMutation = useMutation({
    mutationFn: deleteFooterItem,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: ['MenuItems'],
      });
      toast.success('Voce di menu eliminata!');
      pushMenuToProdMutation.mutate();
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const changeMenuItemOrderMutation = useMutation({
    mutationFn: changeFooterItemsOrder,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: ['MenuItems'],
      });
      toast.success('Voce di menu aggiornate!');
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const pushMenuToProdMutation = useMutation({
    mutationFn: publishFooterItemsInProd,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: ['MenuItemsProd'],
      });
      toast.success('Menu pubblicato in produzione');
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const [showModalFormMenu, setShowModalFormMenu] = useState(false);
  const [menuItemToDelete, setMenuItemToDelete] = useState<null | MenuItem>(
    null
  );
  const [menuItemToUpdate, setMenuItemToUpdate] = useState<null | MenuItem>(
    null
  );
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showModalPushToProd, setShowModalPushToProd] = useState(false);

  const changeMenuItemsOrderHandler = async (orderedMenuItems: MenuItem[]) => {
    const dataToSend = orderedMenuItems.map((m, index) => ({
      id: m.id,
      order: index,
    }));
    return await changeMenuItemOrderMutation.mutateAsync(dataToSend);
  };

  const onConfirmPushMenuInProd = async () => {
    await pushMenuToProdMutation.mutateAsync();
  };

  return (
    <>
      <PageContentLayout
        isLoading={menuItemsIsPending}
        title="Gestione Footer"
        error={menuItemsError}
      >
        <Box sx={{ maxWidth: '800px', m: '0 auto' }}>
          {configEnv.isStage && (
            <Box display="flex" justifyContent="space-between" px={4}>
              {menuItems && menuItems?.length > 1 && (
                <CtaButton
                  variant="contained"
                  onClick={() => setShowModalPushToProd(true)}
                  loading={pagesIsPending}
                  disabled={!!pagesError}
                >
                  Pubblica in prod
                </CtaButton>
              )}
              <Box>
                {menuItems && menuItems?.length > 1 && (
                  <CtaButton
                    onClick={() => setShowOrderModal(true)}
                    loading={pagesIsPending}
                    disabled={!!pagesError}
                  >
                    <LowPriorityIcon /> Ordina
                  </CtaButton>
                )}
                <CtaButton
                  variant="contained"
                  onClick={() => setShowModalFormMenu(true)}
                  loading={pagesIsPending}
                  disabled={!!pagesError}
                >
                  <AddIcon /> Aggiungi voce footer
                </CtaButton>
              </Box>
            </Box>
          )}

          {!menuItemsIsPending && menuItemsError && (
            <Alert severity="error">
              {menuItemsError.message
                ? `Error: ${menuItemsError.message}`
                : 'An Error occurred'}
            </Alert>
          )}

          {menuItems && menuItems?.length === 0 && (
            <EmptySectionMessage title="Inizia aggiungendo una voce di menu" />
          )}

          {menuItems &&
            menuItems.length > 0 &&
            menuItems.map((i) => (
              <MenuItemCard
                key={i.id}
                menuItem={i}
                prodMenuItem={prodMenuItems?.find((mI) => mI.id === i.id)}
                onDeleteClick={() => setMenuItemToDelete(i)}
                onEditClick={() => setMenuItemToUpdate(i)}
              />
            ))}
        </Box>
      </PageContentLayout>
      {pages && (
        <MenuVoiceFormModal
          open={showModalFormMenu || !!menuItemToUpdate}
          handleClose={() => {
            setShowModalFormMenu(false);
            setMenuItemToUpdate(null);
          }}
          pages={pages}
          menuItemToUpdate={menuItemToUpdate}
          createMenuItem={createMenuItemMutation.mutateAsync}
          updateMenuItem={({ menuItemId, data }) =>
            updateMenuItemMutation.mutateAsync({ footerItemId: menuItemId, data })
          }
        />
      )}
      {menuItemToDelete && (
        <MenuVoiceConfirmDeleteModal
          open
          handleClose={() => setMenuItemToDelete(null)}
          text={`Sei sicuro di voler eliminare ${menuItemToDelete?.name}?`}
          onConfirm={() =>
            deleteMenuItemMutation.mutateAsync(menuItemToDelete.id)
          }
        />
      )}

      {menuItems && menuItems?.length > 1 && (
        <MenuVoiceOrderModal
          open={showOrderModal}
          handleClose={() => setShowOrderModal(false)}
          changeMenuItemsOrder={changeMenuItemsOrderHandler}
          menuItems={menuItems}
        />
      )}

      <ConfirmActionModal
        open={showModalPushToProd}
        handleClose={() => setShowModalPushToProd(false)}
        title="Pubblica in produzione"
        description={`Sei sicuro di voler pubblicare il menu in produzione?`}
        onConfirm={onConfirmPushMenuInProd}
      />
    </>
  );
};

export default NavigationFooterPage;
